import { ReactNode, CSSProperties, useState, useEffect } from 'react';
import {
  ControlProps,
  MenuItem,
  Menu,
  DefaultFooter,
  useAutocomplete,
  AutocompleteControl,
} from '@adsk/alloy-react-dropdown';
import Portal from '@adsk/alloy-react-portal';
import type { GenericRecord } from '../../types';
import { translate } from '../../helpers';

export type SingleSelectDropdownProps<T extends GenericRecord> = {
  id?: string;
  options: Array<T>;
  value?: T['value'] | null;
  style?: CSSProperties;
  placeholder?: string;
  searchPlaceholder?: string;
  onChange: (selectedElement: T['value']) => void;
  renderItem?: ControlProps<T>['renderItem'];
  ['data-testid']?: string;
  disabled?: boolean;
  clearable?: boolean;
  renderLeftComponent?: (item: T) => ReactNode;
  error?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  isSearchable?: boolean;
  itemToString?: (item: T | null) => string;
};

export const SingleSelectDropdown = <T extends GenericRecord>({
  id,
  options,
  value = null,
  onChange,
  placeholder = '',
  searchPlaceholder = translate(`defaultSearchPlaceholder`),
  style = {},
  renderItem,
  'data-testid': dataTestId = 'SingleSelectDropdown',
  disabled,
  clearable,
  renderLeftComponent,
  error,
  isOpen: isOpenProp = false,
  onClose,
  itemToString,
}: SingleSelectDropdownProps<T>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpenProp);
  useEffect(() => {
    setIsMenuOpen(isMenuOpen);
  }, [isMenuOpen]);

  const controlledSelectedItem = options.find((option) => option.value === value) || null;
  const {
    controlProps: { selectedItem, ...restControlProps },
    menuProps: { menuItems, getItemProps, highlightedIndex, ...restMenuProps },
    selectItem,
  } = useAutocomplete({
    items: options,
    selectedItem: controlledSelectedItem,
    onSelectedItemChange: (changes) => {
      onChange(changes.selectedItem?.value as T['value']);
    },
    id,
    isOpen: isMenuOpen,
    onIsOpenChange: ({ isOpen: _isOpen = false }) => {
      if (!_isOpen) {
        onClose?.();
      }

      setIsMenuOpen(_isOpen);
    },
    isItemDisabled: (item) => !!item.disabled,
    ...(itemToString ? { itemToString } : {}),
  });

  const onClear = () => {
    onChange('');
    selectItem(null);
  };

  return (
    <div style={{ width: '100%', ...style }}>
      <AutocompleteControl
        disabled={disabled}
        selectedItem={selectedItem}
        placeholder={isMenuOpen ? searchPlaceholder : placeholder}
        renderItem={renderItem}
        data-testid={dataTestId}
        error={error}
        {...restControlProps}
      />
      <Portal container={document.body}>
        <Menu
          renderFooter={() =>
            clearable ? <DefaultFooter disableClear={!selectedItem} onClear={onClear} /> : null
          }
          {...restMenuProps}
        >
          {menuItems.map((item, index) => {
            const leftComponent = () => renderLeftComponent && renderLeftComponent(item);
            return (
              <MenuItem
                key={item.value}
                {...getItemProps?.({
                  item,
                  index,
                })}
                isSelected={selectedItem?.value === item.value}
                isHighlighted={highlightedIndex === index}
                disabled={item.disabled}
                text={item.label}
                subtext={item.subtext}
                renderLeftComponent={leftComponent}
              />
            );
          })}
        </Menu>
      </Portal>
    </div>
  );
};
